import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileUp, CheckCircle2, AlertTriangle, Play, FlaskConical, Rocket } from "lucide-react";
import { useNavigate } from "react-router-dom";

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res((r.result || "").split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function C360Import() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);          // 1=upload  2=map+validate  3=execute
  const [file, setFile] = useState(null);
  const [b64, setB64] = useState("");
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("sandbox");
  const [name, setName] = useState("");
  const [executeResult, setExecuteResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const runPreview = async () => {
    if (!file) return toast.error("Pick a CSV or XLSX");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/imports/c360/preview", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setPreview(r.data);
      setMapping(r.data.suggested_mapping || {});
      const bb = await fileToBase64(file);
      setB64(bb);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const runExecute = async () => {
    if (!Object.values(mapping).includes("contract_number") || !Object.values(mapping).includes("vendor_legal_name")) {
      return toast.error("Mapping must include contract_number and vendor_legal_name");
    }
    setBusy(true);
    try {
      const r = await api.post("/imports/c360/execute", {
        filename: file.name, file_b64: b64, mapping, mode, name,
      });
      setExecuteResult(r.data);
      setStep(3);
      toast.success(`${mode === "sandbox" ? "Sandbox" : "Production"} import: ${r.data.imported} imported, ${r.data.skipped} skipped`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="c360-import">
      <div className="flex items-center gap-3 mb-6">
        <FileUp className="w-6 h-6 text-[#005289]" />
        <div>
          <h1 className="text-xl font-bold">C360 Import</h1>
          <p className="text-sm text-slate-500">Bulk-import contracts from C360 with a field mapping screen, validation report, and sandbox preview.</p>
        </div>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-3 mb-6 text-xs">
        {["Upload CSV/XLSX", "Map fields & validate", "Execute & review"].map((s, i) => (
          <div key={s} className={`px-3 py-1.5 rounded-full font-semibold ${step === i+1 ? "bg-[#005289] text-white" : step > i+1 ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
            {i+1}. {s}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="c360-step1">
          <label className="block mb-3 text-sm font-semibold">Upload C360 CSV or XLSX</label>
          <input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files[0])} className="mb-4" data-testid="c360-file" />
          <div>
            <button onClick={runPreview} disabled={!file || busy} className="h-9 px-4 bg-[#005289] text-white text-sm font-semibold rounded flex items-center gap-2 disabled:opacity-50" data-testid="c360-preview-btn">
              <Play className="w-4 h-4" /> Preview & auto-detect fields
            </button>
          </div>
        </div>
      )}

      {step === 2 && preview && (
        <div className="space-y-4" data-testid="c360-step2">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-sm font-semibold mb-2">Detected {preview.row_count} rows · {(preview.header || []).length} columns</div>
            <div className="grid grid-cols-2 gap-3">
              {(preview.header || []).map(col => (
                <div key={col} className="flex items-center gap-2">
                  <div className="text-xs flex-1 truncate font-mono text-slate-600">{col}</div>
                  <span className="text-slate-400">→</span>
                  <select className="h-8 text-xs px-2 border border-slate-200 rounded flex-1" value={mapping[col] || ""} onChange={e => setMapping({ ...mapping, [col]: e.target.value })} data-testid={`c360-map-${col}`}>
                    <option value="">-- ignore --</option>
                    {(preview.hcd_fields || []).map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {(preview.validation_errors || []).length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-md p-4" data-testid="c360-validation">
              <div className="flex items-center gap-2 mb-2 font-semibold text-amber-900">
                <AlertTriangle className="w-4 h-4" /> Validation ({preview.validation_error_count} error{preview.validation_error_count !== 1 ? "s" : ""})
              </div>
              <div className="max-h-56 overflow-y-auto text-xs text-amber-900 space-y-0.5 font-mono">
                {preview.validation_errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-sm font-semibold mb-2">Sample rows (first 5)</div>
            <div className="max-h-56 overflow-auto text-[11px] font-mono">
              <pre>{JSON.stringify(preview.sample_rows, null, 2)}</pre>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="c360-execute-panel">
            <div className="text-sm font-semibold mb-3">Execute import</div>
            <div className="flex items-center gap-3 mb-3">
              <input className="h-9 px-3 text-sm border border-slate-200 rounded flex-1" placeholder="Batch name (optional)" value={name} onChange={e => setName(e.target.value)} data-testid="c360-batch-name" />
              <div className="flex gap-2">
                <label className={`h-9 px-3 rounded border cursor-pointer flex items-center gap-2 text-sm ${mode === "sandbox" ? "bg-amber-50 border-amber-400 text-amber-900" : "border-slate-200"}`}>
                  <input type="radio" name="mode" value="sandbox" checked={mode === "sandbox"} onChange={e => setMode(e.target.value)} data-testid="c360-mode-sandbox" />
                  <FlaskConical className="w-4 h-4" /> Sandbox
                </label>
                <label className={`h-9 px-3 rounded border cursor-pointer flex items-center gap-2 text-sm ${mode === "production" ? "bg-emerald-50 border-emerald-400 text-emerald-900" : "border-slate-200"}`}>
                  <input type="radio" name="mode" value="production" checked={mode === "production"} onChange={e => setMode(e.target.value)} data-testid="c360-mode-production" />
                  <Rocket className="w-4 h-4" /> Production
                </label>
              </div>
            </div>
            <button onClick={runExecute} disabled={busy} className="h-9 px-4 bg-[#005289] text-white text-sm font-semibold rounded disabled:opacity-50" data-testid="c360-execute-btn">
              <Upload className="w-4 h-4 inline mr-1" /> {mode === "sandbox" ? "Import to sandbox" : "Import to production"}
            </button>
          </div>
        </div>
      )}

      {step === 3 && executeResult && (
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="c360-step3">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 className="w-5 h-5" />
            <div className="font-semibold">Import complete</div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded p-3"><div className="text-[10px] uppercase text-slate-500">Total rows</div><div className="text-2xl font-bold">{executeResult.total_rows}</div></div>
            <div className="bg-emerald-50 rounded p-3"><div className="text-[10px] uppercase text-emerald-700">Imported</div><div className="text-2xl font-bold text-emerald-900">{executeResult.imported}</div></div>
            <div className="bg-amber-50 rounded p-3"><div className="text-[10px] uppercase text-amber-700">Skipped</div><div className="text-2xl font-bold text-amber-900">{executeResult.skipped}</div></div>
            <div className="bg-slate-50 rounded p-3"><div className="text-[10px] uppercase text-slate-500">Mode</div><div className="text-lg font-bold">{executeResult.mode}</div></div>
          </div>
          <div className="text-xs text-slate-600">Batch ID: <span className="font-mono">{executeResult.batch_id}</span></div>
          {executeResult.mode === "sandbox" && (
            <button className="h-9 px-4 bg-emerald-600 text-white text-sm font-semibold rounded" onClick={() => nav(`/admin/c360-import`)} data-testid="c360-view-batches">
              View sandbox batches
            </button>
          )}
          <button className="h-9 px-4 border border-slate-300 text-sm rounded" onClick={() => { setStep(1); setFile(null); setPreview(null); setExecuteResult(null); }}>Start new import</button>
        </div>
      )}

      <BatchesList />
    </div>
  );
}

function BatchesList() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
    const load = async () => {
      try {
        const r = await api.get("/imports/c360/batches");
        setRows(r.data || []);
        setError(null);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load batches");
      }
    };
  useEffect(() => { load(); }, []);

  const promote = async (batch_id) => {
    if (!confirm("Promote this sandbox batch to production?")) return;
    setBusy(true);
    try {
      const r = await api.post(`/imports/c360/batches/${batch_id}/promote`);
      toast.success(`Promoted ${r.data.promoted}, skipped ${r.data.skipped_dupes} duplicates`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-md" data-testid="c360-batches">
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="font-semibold">Recent import batches</div>
        <button onClick={load} className="text-xs px-2 py-1 border border-slate-200 rounded">Refresh</button>
      </div>
      {rows.length === 0 ? (
        error
          ? <div className="p-6 text-sm text-red-700 text-center" data-testid="c360-batches-error">Error: {error}</div>
          : <div className="p-6 text-sm text-slate-500 text-center">No import batches yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs">
            <tr><th className="text-left px-3 py-2">Batch</th><th className="text-left px-3 py-2">Mode</th><th className="text-left px-3 py-2">Imported</th><th className="text-left px-3 py-2">Skipped</th><th className="text-left px-3 py-2">When</th><th className="text-right px-3 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.batch_id} className="border-b border-slate-100" data-testid={`c360-batch-${r.batch_id}`}>
                <td className="px-3 py-2">{r.name} <span className="text-[10px] font-mono text-slate-400">{r.batch_id.slice(0, 8)}</span></td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded ${r.mode === "sandbox" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{r.mode}</span>
                </td>
                <td className="px-3 py-2 text-emerald-700 font-semibold">{r.imported}</td>
                <td className="px-3 py-2 text-amber-700">{r.skipped}</td>
                <td className="px-3 py-2 text-slate-500">{(r.imported_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-2 text-right">
                  {r.mode === "sandbox" && (
                    <button disabled={busy} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded disabled:opacity-50" onClick={() => promote(r.batch_id)} data-testid={`c360-promote-${r.batch_id}`}>Promote to production</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
